import { ElementalTypeEnum, getElementalType } from '../types/elemental'
import type { Elemental, ElementalType } from '../types/elemental'
import type { Rating } from '../types/review'
import { getElementalTypes, removeElementalVote, saveElemental, unsaveElemental, voteElemental } from '@/api/elementals'

export type UpdateElementalState = 'added' | 'removed'
interface UpdateElemental {
  state: UpdateElementalState
  id: string
  nVotes?: number
  nSaves?: number
}

export const useCurrentProductType = (defaultValue?: ElementalType | undefined | null) => useState<ElementalType | undefined | null>('current-product-type', () => defaultValue)
export const useCurrentProductRating = (defaultValue?: Rating | undefined | null) => useState<Rating | undefined | null>('current-product-rating', () => defaultValue)
export const useCurrentProductFeaturedReviewsLength = (defaultValue?: number | undefined | null) => useState<number | undefined | null>('current-product-rating-featured-length', () => defaultValue)

export const useUpdateElementalUpvote = () => useState<null | UpdateElemental>('update-elemental-upvote', () => null)
export const useUpdateElementalSave = () => useState<null | UpdateElemental>('update-elemental-save', () => null)
export const useElementalTypes = () => useState<ElementalType[]>('elemental-types', () => [])

export async function handleElementalUpvote(element?: Elemental, elementalType?: ElementalType | null) {
  if (!element?.id)
    return

  const { firebaseUser } = useFirebaseAuth()
  const route = useRoute()

  if (!firebaseUser.value)
    return navigateTo(useGetLoginURL())

  if (!elementalType)
    elementalType = getElementalType(element, route.path)

  const updater = useUpdateElementalUpvote()
  const nVotes = element.nVotes || 0
  updater.value = {
    state: element.isVoted ? 'removed' : 'added',
    id: element.id,
    nVotes: element.isVoted ? (nVotes - 1) : (nVotes + 1),
  }

  const type = (element.isList || elementalType?.name === 'list') ? 'list' : 'prompt'

  const updaterComposable = type === 'list' ? useUpdateListVote() : useUpdatePromptVote()
  updaterComposable.value = { ...updater.value }

  if (element.isVoted) {
    const success = await removeElementalVote(element.id)
    if (!success)
      return

    sendGTMEvent({ event: `${type}-remove-upvote` })
  }
  else {
    const success = await voteElemental(element.id)
    if (!success)
      return

    sendGTMEvent({ event: `${type}-upvote` })
  }
}

export async function handleElementalSave(element?: Elemental) {
  if (!element?.id)
    return

  const { firebaseUser } = useFirebaseAuth()

  if (!firebaseUser.value)
    return navigateTo(useGetLoginURL())

  const updater = useUpdateElementalSave()

  const nSaves = element.nSaves || 0
  updater.value = {
    state: element.isSaved ? 'removed' : 'added',
    id: element.id,
    nVotes: element.isSaved ? (nSaves - 1) : (nSaves + 1),
  }

  const type = element.isList ? 'list' : 'prompt'
  const functions = {
    save: saveElemental,
    removeSave: unsaveElemental,
  }

  if (element.isList) {
    // Fallback for lists upvote
    const savedListUpdate = useUpdatePromptSave()

    savedListUpdate.value = {
      state: element.isSaved ? 'removed' : 'added',
      id: element.id,
      nVotes: element.isSaved ? (nSaves - 1) : (nSaves + 1),
    }
  }
  else if (element.isPrompt) {
    // Fallback for prompts upvote
    const savedPromptsUpdate = useUpdatePromptSave()

    savedPromptsUpdate.value = {
      state: element.isSaved ? 'removed' : 'added',
      id: element.id,
      nVotes: element.isSaved ? (nSaves - 1) : (nSaves + 1),
    }
  }

  if (element.isSaved) {
    const success = await functions.removeSave(element.id)
    if (!success)
      return

    sendGTMEvent({ event: `${type}-remove-save` })
  }
  else {
    const success = await functions.save(element.id)
    if (!success)
      return

    sendGTMEvent({ event: `${type}-save` })
  }
}

const ForceActive = [ElementalTypeEnum.Image]
const getIsActive = (item: ElementalType) => {
  const { id, metadata } = item
  return {
    ...item,
    metadata: {
      ...metadata,
      isActive: ForceActive.includes(id) || metadata.isActive,
    },
  }
}

export async function handleLoadElementalTypes() {
  const types = useElementalTypes()
  const loaded = await getElementalTypes() || []
  if (process.env.NODE_ENV === 'development')
    types.value = loaded.map(getIsActive)
  else
    types.value = loaded
}
